import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userManager } from '../../config/oidc';
import { accessibleOrgIds } from '../../helpers/orgClaims';
import type { Entitlement } from '../../types/entitlement';

// Result of a successful org fetch: the member-scoped list plus the set of org
// segments the JWT says the user may act within (member + cross-tenant granted).
// The latter validates the active segment so a granted-org deep-link survives.
export interface FetchOrganizationsResult {
  data: Organization[];
  accessibleOrgIds: string[];
}

export interface OrganizationConfig {
  // Root-category names enabling per-tenant asset categorization
  // (e.g. ["furniture", "machine"]). Semantics:
  //   - null/undefined → never configured; menu falls back to ["furniture"].
  //   - []             → explicitly opted out; only always-active common
  //                      taxonomy roots (Model, Manufacturer) appear.
  //   - [...]          → use as-is.
  assetTaxonomies?: string[];
  // ISO 3166-1 alpha-2 tax jurisdiction; null/undefined → backend default (NO).
  jurisdiction?: string;
  [key: string]: unknown;
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  organizationNumber?: string;
  address?: {
    streetAddress?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    geoJson?: {
      type: string;
      coordinates: [number, number];
    } | null;
  };
  config?: OrganizationConfig;
  services?: Record<string, unknown>;
  // Subscription tier (e.g. "ASSET_MANAGEMENT"); a compatibility projection over
  // `entitlements` (backend `OrganizationDto.subscriptionLevel`) that collapses to
  // one plan and so cannot represent an organization holding several products.
  // No longer read for permission gating (see `entitlements` below) — kept for
  // other readers.
  subscriptionLevel?: string | null;
  // The products this organization actually holds, one row per app, as returned by
  // `GET /?permission=READ_ORGANIZATION` (`OrganizationDto.entitlements`). Drives
  // which permissions are assignable in PermissionSelector via
  // `ranksFromEntitlements` — the fix for what `subscriptionLevel` above cannot
  // express. Three states matter (permission-gate-from-entitlements D3):
  //   - undefined → UNKNOWN (an older backend omitting the field, or not yet
  //     fetched): the gate stays permissive rather than treating it as "holds
  //     nothing".
  //   - []        → a DEFINITE answer: the organization holds nothing, and
  //     product-gated permissions are withheld.
  //   - [...]     → the organization's actual holdings, unioned across apps.
  entitlements?: Entitlement[];
}

interface OrganizationsState {
  data: Organization[];
  loading: boolean;
  error: string | null;
  sessionExpired: boolean;
  dynamicSegment: string;
  /** True once a fetch has settled, however it settled. Distinguishes "none" from "not yet". */
  fetched: boolean;
}

const initialState: OrganizationsState = {
  data: [],
  loading: false,
  error: null,
  sessionExpired: false,
  dynamicSegment: '', // Initial value will be loaded from localStorage via separate init logic
  fetched: false,
};

export const fetchOrganizations = createAsyncThunk(
  'organizations/fetchOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      const apiUrl = import.meta.env.VITE_API_ORGADMIN_API_BASE || 'https://test-api.sirktek.com/orgadmin/v1';
      const urlWithParams = `${apiUrl}?permission=READ_ORGANIZATION`;

      // Get access token from OIDC user manager
      const user = await userManager.getUser();
      const token = user?.access_token;
      if (!token) {
        return rejectWithValue({ message: 'Not authenticated', sessionExpired: true });
      }

      const response = await fetch(urlWithParams, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      return {
        data: json as Organization[],
        accessibleOrgIds: accessibleOrgIds(user),
      } satisfies FetchOrganizationsResult;
    } catch (err) {
      if (
        err instanceof Error &&
        (
          err.message.includes('login_required') ||
          err.message.includes('invalid_grant') ||
          err.message.includes('consent_required') ||
          err.message.includes('unauthorized') ||
          err.message.includes('401') ||
          err.message.includes('403')
        )
      ) {
        return rejectWithValue({
          message: err.message,
          sessionExpired: true
        });
      }

      return rejectWithValue({
        message: err instanceof Error ? err.message : 'Unknown error',
        sessionExpired: false
      });
    }
  }
);

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    clearOrganizationsError: (state) => {
      state.error = null;
    },
    resetSessionExpired: (state) => {
      state.sessionExpired = false;
    },
    setDynamicSegment: (state, action: PayloadAction<string>) => {
      state.dynamicSegment = action.payload;
      // localStorage side effect handled by middleware
    },
    clearDynamicSegment: (state) => {
      state.dynamicSegment = '';
      // localStorage side effect handled by middleware
    },
    // Replace a single org in `data` after an in-place edit (e.g. settings
    // save) so consumers that subscribe to this slice — notably STMenu, which
    // reads `config.assetTaxonomies` — re-render without waiting for the next
    // full `fetchOrganizations()` cycle. No-op when the org isn't already in
    // the list, since settings edits can't introduce a new tenant.
    upsertOrganization: (state, action: PayloadAction<Organization>) => {
      const idx = state.data.findIndex((o) => o.id === action.payload.id);
      if (idx >= 0) {
        state.data[idx] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action: PayloadAction<FetchOrganizationsResult>) => {
        state.loading = false;
        state.fetched = true;
        const { data, accessibleOrgIds: accessibleIds } = action.payload;
        state.data = data;

        if (data.length > 0) {
          // The token is the authority on selectability, not the fetched listing. The
          // listing is member-scoped and app-blind; the token is app-scoped
          // (sirk-auth-backend #45), so it omits both organizations the user cannot act
          // within and organizations whose tenant bought a different product than this
          // build sells. Selecting one of the latter lands on /no-access, since the
          // landing route resolves against these same token permissions.
          //
          // It also *adds* cross-tenant granted orgs, which the member-scoped listing
          // never returns — validating against the listing alone would discard a
          // legitimate `?org=<granted-org>` deep-link and snap the user back home.
          //
          // A token carrying no org claims at all (pre-claim issuance) yields no ids;
          // fall back to the listing then rather than stranding the user with nothing.
          const selectable =
            accessibleIds.length > 0
              ? data.filter((org) => accessibleIds.includes(org.id))
              : data;
          const isValidSegment =
            !!state.dynamicSegment &&
            (accessibleIds.length > 0
              ? accessibleIds.includes(state.dynamicSegment)
              : data.some((org) => org.id === state.dynamicSegment));
          if (!isValidSegment) {
            // A member org usable in this app first; failing that any org the token
            // names (a grantee whose own memberships are all out of scope here).
            state.dynamicSegment = selectable[0]?.id ?? accessibleIds[0] ?? data[0].id;
            // localStorage side effect handled by middleware
          }
        }
        // data.length === 0 (no memberships): leave the segment untouched so a
        // grantee-only user reaching a granted org via `?org=` keeps it.
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.fetched = true;
        if (action.payload) {
          const payload = action.payload as { message: string, sessionExpired: boolean };
          state.error = payload.message;
          state.sessionExpired = payload.sessionExpired;
        } else {
          state.error = action.error.message || 'Unknown error';
        }
      });
  },
});

export const {
  clearOrganizationsError,
  resetSessionExpired,
  setDynamicSegment,
  clearDynamicSegment,
  upsertOrganization,
} = organizationsSlice.actions;
export default organizationsSlice.reducer;